import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import AppNavbar from "./AppNavbar.js";
import SectionCard from "./SectionCard.js";
import {
  buildLoginUrl,
  buildLogoutUrl,
  buildSignupUrl,
  getTokenStorageKey,
  isTokenExpired,
  readTokenFromUrlHash,
} from "./auth.js";
import { catalogFetch } from "./catalogApi.js";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_REDIRECT_URI = `${window.location.origin}/open-orders`;
const COGNITO_LOGOUT_URI = `${window.location.origin}/open-orders`;

function formatCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatTimestamp(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "Unknown";
  }
  return new Date(numeric * 1000).toLocaleString();
}

function OpenOrdersPage() {
  const [idToken, setIdToken] = useState("");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState({ cash_available: "0.00", cash_reserved: "0.00" });
  const [orders, setOrders] = useState([]);
  const [marketTitlesById, setMarketTitlesById] = useState({});
  const [loading, setLoading] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const tokenFromHash = readTokenFromUrlHash();
    const storedToken = localStorage.getItem(getTokenStorageKey()) || "";
    const token = tokenFromHash || storedToken;

    if (!token || isTokenExpired(token)) {
      localStorage.removeItem(getTokenStorageKey());
      setIdToken("");
      return;
    }

    localStorage.setItem(getTokenStorageKey(), token);
    setIdToken(token);
    if (tokenFromHash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loginUrl = useMemo(
    () => buildLoginUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const signupUrl = useMemo(
    () => buildSignupUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const logoutUrl = useMemo(
    () => buildLogoutUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_LOGOUT_URI),
    []
  );

  const apiFetch = useCallback(
    async (path, options = {}) => {
      if (!API_BASE_URL) {
        throw new Error("Missing REACT_APP_API_BASE_URL");
      }
      if (!idToken) {
        throw new Error("Not signed in");
      }
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: options.method || "GET",
        body: options.body,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
          ...(options.headers || {}),
        },
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.error || body.message || "Request failed");
      }
      return body;
    },
    [idToken]
  );

  const loadPage = useCallback(async () => {
    if (!idToken) {
      setMe(null);
      setBalance({ cash_available: "0.00", cash_reserved: "0.00" });
      setOrders([]);
      setMarketTitlesById({});
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [meRes, balanceRes, ordersRes, marketsRes] = await Promise.all([
        apiFetch("/me"),
        apiFetch("/me/balance"),
        apiFetch("/me/orders?limit=100"),
        catalogFetch("/markets", idToken),
      ]);

      setMe(meRes);
      setBalance(balanceRes);
      setOrders(Array.isArray(ordersRes.items) ? ordersRes.items : []);

      const marketTitles = {};
      const marketItems = Array.isArray(marketsRes.items) ? marketsRes.items : [];
      marketItems.forEach((market) => {
        if (!market?.market_id) {
          return;
        }
        marketTitles[market.market_id] = market.option_label || market.title || market.market_id;
      });
      setMarketTitlesById(marketTitles);
    } catch (err) {
      setError(err.message || "Failed to load open orders");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, idToken]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      await loadPage();
      if (cancelled) {
        return;
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [loadPage]);

  const handleLogin = () => {
    if (!loginUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(loginUrl);
  };

  const handleSignup = () => {
    if (!signupUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(signupUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem(getTokenStorageKey());
    setIdToken("");
    setMe(null);
    setBalance({ cash_available: "0.00", cash_reserved: "0.00" });
    setOrders([]);
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!orderId) {
      setError("Missing order_id");
      return;
    }

    setCancellingOrderId(orderId);
    setError("");
    try {
      await apiFetch(`/orders/${encodeURIComponent(orderId)}`, {
        method: "DELETE",
      });
      await loadPage();
    } catch (err) {
      setError(err.message || "Failed to cancel order");
    } finally {
      setCancellingOrderId("");
    }
  };

  const openOrders = useMemo(
    () =>
      orders
        .filter((order) => {
          const status = String(order.status || "").toUpperCase();
          const remainingQty = Number(order.remaining_qty);
          if (status === "FILLED" || status === "CANCELLED") {
            return false;
          }
          return Number.isFinite(remainingQty) && remainingQty > 0;
        })
        .sort((left, right) => Number(right.created_at || 0) - Number(left.created_at || 0)),
    [orders]
  );

  return (
    <div className="App">
      <AppNavbar
        links={[
          { href: "/", label: "Home" },
          { href: "/open-orders", label: "Open Orders" },
          { href: "/communities", label: "Communities" },
          { href: "/leaderboard", label: "Leaderboard" },
          { href: "/market-resolution", label: "Resolve Market" },
          { href: "/markets/new", label: "Create Market" },
        ]}
        isSignedIn={Boolean(idToken)}
        me={me}
        balance={balance}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onSignup={handleSignup}
      />
      <Container className="py-4">
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" className="mb-0">{error}</Alert>
            </Col>
          </Row>
        )}

        {!idToken ? (
          <Row>
            <Col>
              <Alert variant="info" className="mb-0">
                Sign in to view your active orders.
              </Alert>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col>
              <SectionCard title="Open Orders">
                <div className="open-orders-page-copy">
                  <div className="open-orders-page-kicker">Active tickets</div>
                  <h2>Placed Bets</h2>
                  <p>Track every still-open order in one place, with stake, payout, and open contracts at a glance.</p>
                </div>

                {loading ? (
                  <Alert variant="secondary" className="mb-0">Loading open orders...</Alert>
                ) : openOrders.length === 0 ? (
                  <Alert variant="secondary" className="mb-0">You do not have any open orders right now.</Alert>
                ) : (
                  <div className="open-order-card-grid">
                    {openOrders.map((order) => {
                      const qty = Number(order.quantity || 0);
                      const remainingQty = Number(order.remaining_qty || 0);
                      const priceTicks = Number(order.limit_price || 0);
                      const totalPlaced = (priceTicks / 100) * qty;
                      const openAmount = (priceTicks / 100) * remainingQty;
                      const potentialPayout = qty;
                      return (
                        <article key={order.order_id} className="open-order-bet-card">
                          <div className="open-order-bet-card-top">
                            <div>
                              <div className="open-order-bet-eyebrow">Open order</div>
                              <h3>{marketTitlesById[order.market_id] || order.market_id}</h3>
                            </div>
                            <Badge bg="secondary" className="open-order-bet-status">
                              {order.status || "OPEN"}
                            </Badge>
                          </div>

                          <div className="open-order-bet-selection">
                            <span className={`open-order-outcome-pill open-order-outcome-${String(order.outcome || "").toLowerCase()}`}>
                              {order.outcome || "-"}
                            </span>
                            <span className="open-order-contract-copy">
                              {remainingQty} of {qty} contracts still open
                            </span>
                          </div>

                          <div className="open-order-bet-metrics">
                            <div className="open-order-bet-metric">
                              <span className="open-order-bet-label">Total placed</span>
                              <strong>{formatCurrency(totalPlaced)}</strong>
                            </div>
                            <div className="open-order-bet-metric">
                              <span className="open-order-bet-label">Potential payout</span>
                              <strong>{formatCurrency(potentialPayout)}</strong>
                            </div>
                            <div className="open-order-bet-metric">
                              <span className="open-order-bet-label">Contracts</span>
                              <strong>{qty}</strong>
                            </div>
                          </div>

                          <div className="open-order-bet-footer">
                            <div className="open-order-bet-footer-row">
                              <span>Price</span>
                              <span>{priceTicks}c</span>
                            </div>
                            <div className="open-order-bet-footer-row">
                              <span>Open amount</span>
                              <span>{formatCurrency(openAmount)}</span>
                            </div>
                            <div className="open-order-bet-footer-row">
                              <span>Placed</span>
                              <span>{formatTimestamp(order.created_at)}</span>
                            </div>
                          </div>
                          <div className="open-order-bet-actions">
                            <Button
                              type="button"
                              variant="outline-danger"
                              className="open-order-bet-cancel-btn"
                              disabled={loading || cancellingOrderId === order.order_id}
                              onClick={() => handleCancelOrder(order.order_id)}
                            >
                              {cancellingOrderId === order.order_id ? "Cancelling..." : "Cancel Order"}
                            </Button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </SectionCard>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}

export default OpenOrdersPage;
